/**
 * JWT Token Utilities
 * 
 * Utilities for handling JWT token validation, expiration checks,
 * and authentication state management.
 */

interface JWTPayload {
  sub: string;
  exp: number;
  iat: number;
  user_id?: string;
  institution_id?: string;
}

/**
 * Decode JWT token payload without verification
 * @param token JWT token string
 * @returns Decoded payload or null if invalid
 */
export const decodeJWTPayload = (token: string): JWTPayload | null => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3 || !parts[1]) {
      return null;
    }

    // Decode base64 payload
    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded) as JWTPayload;
  } catch (error) {
    console.error('Error decoding JWT token:', error);
    return null;
  }
};

/**
 * Check if JWT token is expired
 * @param token JWT token string
 * @param bufferMinutes Optional buffer time in minutes before actual expiration
 * @returns true if token is expired or invalid
 */
export const isTokenExpired = (token: string | null, bufferMinutes: number = 5): boolean => {
  if (!token) {
    return true;
  }

  try {
    const payload = decodeJWTPayload(token);
    if (!payload || !payload.exp) {
      return true;
    }

    // Convert expiration to milliseconds and add buffer
    const expirationTime = payload.exp * 1000;
    const bufferTime = bufferMinutes * 60 * 1000;
    const now = Date.now();

    return now >= (expirationTime - bufferTime);
  } catch (error) {
    console.error('Error checking token expiration:', error);
    return true;
  }
};

/**
 * Get time until token expiration
 * @param token JWT token string
 * @returns Minutes until expiration, or 0 if expired/invalid
 */
export const getTimeUntilExpiration = (token: string | null): number => {
  if (!token) {
    return 0;
  }

  try {
    const payload = decodeJWTPayload(token);
    if (!payload || !payload.exp) {
      return 0;
    }

    const expirationTime = payload.exp * 1000;
    const now = Date.now();
    const timeLeft = expirationTime - now;

    return timeLeft > 0 ? Math.floor(timeLeft / (60 * 1000)) : 0;
  } catch (error) {
    console.error('Error calculating time until expiration:', error);
    return 0;
  }
};

/**
 * Format expiration time for display
 * @param token JWT token string
 * @returns Formatted string showing expiration time
 */
export const formatTokenExpiration = (token: string | null): string => {
  if (!token) {
    return 'Token no válido';
  }

  const minutesLeft = getTimeUntilExpiration(token);
  
  if (minutesLeft <= 0) {
    return 'Token expirado';
  }

  if (minutesLeft < 60) {
    return `Expira en ${minutesLeft} minutos`;
  }

  const hoursLeft = Math.floor(minutesLeft / 60);
  const remainingMinutes = minutesLeft % 60;
  
  return `Expira en ${hoursLeft}h ${remainingMinutes}m`;
};
